// Lightweight in-browser diagnostic capture used by the bug-report widget.
//
// Initialised once at app boot via `initDiagCapture()`. Maintains rolling
// buffers of the **last 20 network calls** (same-origin / fastn API only —
// fonts, images, third-party CDNs are filtered out) and **last 20 console
// errors**. For each network call we capture method, URL, status, duration,
// x-request-id, and (best-effort, redacted, capped) request + response bodies.
//
// Redaction (always-on):
//   - Auth headers stripped: Authorization, Cookie, Set-Cookie, X-Api-Key
//   - JSON fields matching /password|secret|token|api[-_]?key|private[-_]?key/i
//     are replaced with "[redacted]"
//   - Bodies capped at 2 KB; binary content-types replaced with "[binary]"
//
// `getDiagSnapshot()` returns a frozen copy of the buffers + browser context
// for the bug-report submit handler.

const NETWORK_LIMIT = 10;
const CONSOLE_LIMIT = 20;
const URL_MAX_LENGTH = 1500;
const BODY_MAX_LENGTH = 2048;

const SENSITIVE_FIELD_RE = /password|secret|token|api[-_]?key|private[-_]?key|auth/i;
const BINARY_CONTENT_TYPE_RE = /^(image\/|video\/|audio\/|application\/(octet-stream|pdf|zip|gzip|x-protobuf))/i;

export interface NetworkCall {
  method: string;
  url: string;
  status: number;
  durationMs: number;
  requestId?: string;
  timestamp: string;
  /** Redacted, capped request body (best-effort). */
  requestBody?: string;
  /** Redacted, capped response body (best-effort). */
  responseBody?: string;
  /** Selected request headers (auth headers stripped). */
  requestHeaders?: Record<string, string>;
  /** Selected response headers (auth headers stripped). */
  responseHeaders?: Record<string, string>;
}

export interface ConsoleEntry {
  level: "error" | "warn";
  message: string;
  timestamp: string;
}

export interface BrowserContext {
  url: string;
  userAgent: string;
  viewport: { width: number; height: number };
  buildSha?: string;
}

export interface DiagSnapshot {
  network: NetworkCall[];
  consoleLog: ConsoleEntry[];
  browser: BrowserContext;
}

const networkBuffer: NetworkCall[] = [];
const consoleBuffer: ConsoleEntry[] = [];

let initialised = false;

// Sensitive header names — never included in the captured headers map.
const STRIPPED_HEADER_NAMES = new Set([
  "authorization", "cookie", "set-cookie", "x-api-key",
  "proxy-authorization", "www-authenticate", "x-csrf-token",
]);

// Path patterns that are auth/identity infrastructure, not application logic —
// not useful when debugging a feature. Skip even though host may match.
const SKIP_PATH_PATTERNS = [
  /\/auth\/realms\//i,
  /\/protocol\/openid-connect\//i,
  /\/.well-known\//i,
];

/**
 * Should this URL be captured? Keeps fastn API traffic (same-host any port,
 * *.fastn.ai, anything under an /api/vN/, /v1/ or /v2/ path) and drops static
 * assets, fonts, analytics, and Keycloak auth infrastructure.
 *
 * Exported for unit testing — not part of the runtime contract.
 */
export function shouldCaptureUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url, window.location.origin);
  } catch {
    return true; // Relative path we couldn't parse — capture
  }

  // Drop auth infra (Keycloak, .well-known, etc.) regardless of host.
  for (const re of SKIP_PATH_PATTERNS) if (re.test(parsed.pathname)) return false;

  // Same-hostname (any port) — covers localhost:3000 from a localhost:5174 dashboard.
  if (parsed.hostname === window.location.hostname) return true;

  // Any fastn.ai subdomain (dev/prod APIs).
  if (parsed.hostname === "fastn.ai" || parsed.hostname.endsWith(".fastn.ai")) return true;

  // Catch-all: anything that looks like a fastn API path. This is the only rule
  // that keeps app.fastn.dev's calls to api.fastn.dev, which share no hostname
  // and are not *.fastn.ai, so it has to know /v1 and /v2, the spelling the
  // dashboard calls (KAT-8371).
  if (/^\/(api\/v\d+|v1|v2)\//.test(parsed.pathname)) return true;

  return false;
}

/** Build a redacted headers map from a Headers / Record / array. */
function captureHeaders(input: HeadersInit | Headers | undefined | null): Record<string, string> | undefined {
  if (!input) return undefined;
  const out: Record<string, string> = {};
  const set = (k: string, v: string) => {
    const lower = k.toLowerCase();
    if (STRIPPED_HEADER_NAMES.has(lower)) return;
    out[lower] = v.length > 200 ? v.slice(0, 199) + "…" : v;
  };
  if (input instanceof Headers) {
    input.forEach((v, k) => set(k, v));
  } else if (Array.isArray(input)) {
    for (const [k, v] of input) set(k, v);
  } else {
    for (const [k, v] of Object.entries(input)) set(k, String(v));
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Capture all headers off a Response. */
function captureResponseHeaders(res: Response): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  res.headers.forEach((v, k) => {
    const lower = k.toLowerCase();
    if (STRIPPED_HEADER_NAMES.has(lower)) return;
    out[lower] = v.length > 200 ? v.slice(0, 199) + "…" : v;
  });
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Parse the raw "name: value\r\n" string from XHR.getAllResponseHeaders(). */
function parseXhrResponseHeaders(raw: string): Record<string, string> | undefined {
  if (!raw) return undefined;
  const out: Record<string, string> = {};
  for (const line of raw.split(/\r?\n/)) {
    const idx = line.indexOf(":");
    if (idx < 0) continue;
    const k = line.slice(0, idx).trim().toLowerCase();
    if (!k || STRIPPED_HEADER_NAMES.has(k)) continue;
    const v = line.slice(idx + 1).trim();
    out[k] = v.length > 200 ? v.slice(0, 199) + "…" : v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function initDiagCapture(): void {
  if (initialised) return;
  initialised = true;

  // ── fetch ────────────────────────────────────────────────────────────────
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const start = performance.now();
    const method = (init?.method ?? (typeof input === "object" && "method" in input ? input.method : "GET")).toUpperCase();
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;

    if (!shouldCaptureUrl(url)) {
      return originalFetch(input, init);
    }

    const requestBody = extractRequestBodyFromInit(init);
    const requestHeaders = captureHeaders(init?.headers ?? (typeof input === "object" && "headers" in input ? input.headers : undefined));

    let response: Response | undefined;
    let status = 0;
    let responseBody: string | undefined;
    let responseHeaders: Record<string, string> | undefined;
    try {
      response = await originalFetch(input, init);
      status = response.status;
      responseHeaders = captureResponseHeaders(response);
      // Best-effort response body capture via clone — never blocks the consumer.
      responseBody = await readResponseBodySafely(response);
      return response;
    } catch (err) {
      status = 0;
      throw err;
    } finally {
      pushNetwork({
        method,
        url: truncate(url, URL_MAX_LENGTH),
        status,
        durationMs: Math.round(performance.now() - start),
        requestId: response?.headers.get("x-request-id") ?? undefined,
        timestamp: new Date().toISOString(),
        requestBody,
        responseBody,
        requestHeaders,
        responseHeaders,
      });
    }
  };

  // ── XMLHttpRequest ───────────────────────────────────────────────────────
  type XhrDiag = { method: string; url: string; start: number; capture: boolean; requestBody?: string; requestHeaders: Record<string, string> };
  const OriginalXhrOpen = XMLHttpRequest.prototype.open;
  const OriginalXhrSend = XMLHttpRequest.prototype.send;
  const OriginalXhrSetHeader = XMLHttpRequest.prototype.setRequestHeader;
  XMLHttpRequest.prototype.open = function (
    this: XMLHttpRequest & { __diag?: XhrDiag },
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ): void {
    const fullUrl = typeof url === "string" ? url : url.toString();
    this.__diag = {
      method: method.toUpperCase(),
      url: fullUrl,
      start: 0,
      capture: shouldCaptureUrl(fullUrl),
      requestHeaders: {},
    };
    return OriginalXhrOpen.apply(this, [method, url, ...rest] as Parameters<typeof OriginalXhrOpen>);
  };
  XMLHttpRequest.prototype.setRequestHeader = function (
    this: XMLHttpRequest & { __diag?: XhrDiag },
    name: string,
    value: string,
  ): void {
    if (this.__diag) {
      const lower = name.toLowerCase();
      if (!STRIPPED_HEADER_NAMES.has(lower)) {
        this.__diag.requestHeaders[lower] = value.length > 200 ? value.slice(0, 199) + "…" : value;
      }
    }
    return OriginalXhrSetHeader.call(this, name, value);
  };
  XMLHttpRequest.prototype.send = function (
    this: XMLHttpRequest & { __diag?: XhrDiag },
    body?: Document | XMLHttpRequestBodyInit | null,
  ): void {
    if (this.__diag) {
      this.__diag.start = performance.now();
      this.__diag.requestBody = body == null ? undefined : extractRequestBodyValue(body);
    }
    if (this.__diag?.capture) {
      this.addEventListener("loadend", () => {
        if (!this.__diag) return;
        const responseContentType = safeGetResponseHeader(this, "content-type") ?? null;
        const responseBody = sanitizeBody(
          typeof this.responseText === "string" ? this.responseText : "",
          responseContentType,
        );
        const responseHeaders = parseXhrResponseHeaders(this.getAllResponseHeaders());
        pushNetwork({
          method: this.__diag.method,
          url: truncate(this.__diag.url, URL_MAX_LENGTH),
          status: this.status,
          durationMs: Math.round(performance.now() - this.__diag.start),
          requestId: safeGetResponseHeader(this, "x-request-id"),
          timestamp: new Date().toISOString(),
          requestBody: sanitizeBody(this.__diag.requestBody ?? "", null),
          responseBody,
          requestHeaders: Object.keys(this.__diag.requestHeaders).length > 0 ? this.__diag.requestHeaders : undefined,
          responseHeaders,
        });
      });
    }
    return OriginalXhrSend.call(this, body);
  };

  // ── console.error only ───────────────────────────────────────────────────
  // We deliberately skip console.warn — it's mostly Vite/HMR/library noise.
  const originalError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    pushConsole("error", args);
    originalError(...args);
  };
}

function pushNetwork(call: NetworkCall): void {
  networkBuffer.push(call);
  if (networkBuffer.length > NETWORK_LIMIT) networkBuffer.shift();
}

function pushConsole(level: "error" | "warn", args: unknown[]): void {
  consoleBuffer.push({
    level,
    message: truncate(args.map(formatArg).join(" "), 1800),
    timestamp: new Date().toISOString(),
  });
  if (consoleBuffer.length > CONSOLE_LIMIT) consoleBuffer.shift();
}

function formatArg(a: unknown): string {
  if (a instanceof Error) return `${a.name}: ${a.message}`;
  if (typeof a === "string") return a;
  try {
    return JSON.stringify(a);
  } catch {
    return String(a);
  }
}

function safeGetResponseHeader(xhr: XMLHttpRequest, name: string): string | undefined {
  try {
    return xhr.getResponseHeader(name) ?? undefined;
  } catch {
    return undefined;
  }
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1) + "…";
}

/**
 * Pull a string view of `init.body` if it's a JSON-serializable form.
 * FormData / Blob / Stream payloads are summarized rather than dumped.
 */
function extractRequestBodyFromInit(init?: RequestInit): string | undefined {
  if (!init?.body) return undefined;
  return sanitizeBody(extractRequestBodyValue(init.body), getInitContentType(init));
}

function extractRequestBodyValue(body: BodyInit | Document): string {
  if (typeof body === "string") return body;
  if (body instanceof URLSearchParams) return body.toString();
  if (body instanceof FormData) {
    const parts: string[] = [];
    body.forEach((v, k) => parts.push(`${k}=${typeof v === "string" ? v : "[file]"}`));
    return parts.join("&");
  }
  if (body instanceof Blob) return `[blob ${body.size} bytes type=${body.type || "unknown"}]`;
  if (body instanceof ArrayBuffer) return `[arraybuffer ${body.byteLength} bytes]`;
  return "[unsupported body type]";
}

function getInitContentType(init: RequestInit): string | null {
  const headers = init.headers;
  if (!headers) return null;
  if (headers instanceof Headers) return headers.get("content-type");
  if (Array.isArray(headers)) {
    for (const [k, v] of headers) if (k.toLowerCase() === "content-type") return v;
    return null;
  }
  for (const [k, v] of Object.entries(headers)) if (k.toLowerCase() === "content-type") return String(v);
  return null;
}

async function readResponseBodySafely(res: Response): Promise<string | undefined> {
  try {
    const contentType = res.headers.get("content-type");
    if (contentType && BINARY_CONTENT_TYPE_RE.test(contentType)) return "[binary]";
    // Server-sent events: clone().text() never resolves while the stream is open.
    if (contentType && /text\/event-stream/i.test(contentType)) return "[stream]";
    const clone = res.clone();
    const text = await clone.text();
    return sanitizeBody(text, contentType);
  } catch {
    return undefined;
  }
}

/**
 * Strip secrets, redact sensitive JSON fields, cap size.
 *
 * Exported for unit testing — not part of the runtime contract.
 */
export function sanitizeBody(body: string, contentType: string | null): string {
  if (!body) return "";
  if (contentType && BINARY_CONTENT_TYPE_RE.test(contentType)) return "[binary]";
  let processed = body;
  // Field-level redaction for JSON
  try {
    const obj = JSON.parse(body);
    redactObjectFields(obj);
    processed = JSON.stringify(obj);
  } catch {
    // Not JSON — redact form-style key=value occurrences inline
    processed = body.replace(
      /(password|secret|token|api[-_]?key|private[-_]?key|authorization)([^=&]{0,40}?=)([^&\s]*)/gi,
      "$1$2[redacted]",
    );
  }
  if (processed.length > BODY_MAX_LENGTH) {
    processed = processed.slice(0, BODY_MAX_LENGTH) + "…[truncated]";
  }
  return processed;
}

function redactObjectFields(obj: unknown): void {
  if (!obj || typeof obj !== "object") return;
  if (Array.isArray(obj)) {
    for (const item of obj) redactObjectFields(item);
    return;
  }
  const o = obj as Record<string, unknown>;
  for (const k of Object.keys(o)) {
    if (SENSITIVE_FIELD_RE.test(k)) {
      o[k] = "[redacted]";
    } else if (typeof o[k] === "object") {
      redactObjectFields(o[k]);
    }
  }
}

/** Snapshot of captured diagnostic data for shipping with a bug report. */
export function getDiagSnapshot(): DiagSnapshot {
  return {
    network: networkBuffer.slice(),
    consoleLog: consoleBuffer.slice(),
    browser: {
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      buildSha: import.meta.env.VITE_BUILD_SHA as string | undefined,
    },
  };
}

/** Clear buffers — useful between tests or after a successful submit. */
export function resetDiagCapture(): void {
  networkBuffer.length = 0;
  consoleBuffer.length = 0;
}
